import {
    BatteryStorageInactiveTroubleshooting,
    SystemTier1DefaultTroubleshooting,
    HighImpactAlert7Troubleshooting,
    HighImpactAlert8Troubleshooting,
    HighImpactAlert9Troubleshooting,
    ProductionIssueTroubleshooting,
    MeterIssueTroubleshooting,
    IconBatteryEmpty,
    IconGitBranch01,
    IconGitConnect,
    IconGrid,
    IconLightning02,
    IconMessageXCircle,
    IconModem,
    IconSlashCircle01,
    IconSpeedometer,
    IconSpeedometer04,
    IconTool01,
    IconWifiOff,
    IconSize
} from '@/components'
import { InverterManufacturerEnum, SystemStatusEnum } from '@/enums'
import { feature } from '@/services'
import { SystemStatusDataType } from '@/types'

export const SYSTEM_STATUSES_MONITORED_TIER_1: SystemStatusDataType[] = [
    {
        value: SystemStatusEnum.POWER,
        title: 'Production Issue',
        refreshable: true,
        secondaryOrder: 1,
        icon: (size: IconSize) => <IconLightning02 size={size}/>,
        troubleshooting: <ProductionIssueTroubleshooting/>,
        description: 'System production is below expected levels, indicating potential hardware issues or setting' +
            ' inaccuracies.'
    },
    {
        value: SystemStatusEnum.IMPACT_ALERT_LEVEL_9,
        title: 'High Impact Alert',
        refreshable: true,
        secondaryOrder: 5,
        icon: () => 9,
        troubleshooting: <HighImpactAlert9Troubleshooting/>,
        description: 'Critical alert with maximum severity score, indicating substantial energy loss or system risk.' +
            ' For detailed information, please visit your SolarEdge Monitoring portal. Solar Insure only retrieves' +
            ' the  Impact Indicator score (1-9), calculated by SolarEdge based on potential energy loss.'
    },
    {
        value: SystemStatusEnum.IMPACT_ALERT_LEVEL_8,
        title: 'High Impact Alert',
        refreshable: true,
        secondaryOrder: 6,
        icon: () => 8,
        troubleshooting: <HighImpactAlert8Troubleshooting/>,
        description: 'Urgent alert with high severity score, denoting significant risk and requiring immediate' +
            ' attention. For detailed information, please visit your SolarEdge Monitoring portal. Solar Insure only' +
            ' retrieves the Impact Indicator score (1-9), calculated by SolarEdge based on potential energy loss.'
    },
    {
        value: SystemStatusEnum.IMPACT_ALERT_LEVEL_7,
        title: 'High Impact Alert',
        refreshable: true,
        secondaryOrder: 7,
        icon: () => 7,
        troubleshooting: <HighImpactAlert7Troubleshooting/>,
        description: 'Serious alert with elevated impact score, signifying notable energy loss or hazard. For' +
            ' detailed information, please visit your SolarEdge Monitoring portal. Solar Insure only retrieves the' +
            ' Impact Indicator score (1-9), calculated by SolarEdge based on potential energy loss.'
    },
    {
        value: SystemStatusEnum.METER_ISSUE,
        title: 'Meter Issue',
        refreshable: true,
        secondaryOrder: 2,
        icon: (size: IconSize) => <IconSpeedometer size={size}/>,
        troubleshooting: <MeterIssueTroubleshooting/>,
        description: 'Possible problem with measurement equipment, likely affecting data accuracy and requiring' +
            ' on-site maintenance.'
    },
    {
        value: SystemStatusEnum.STORAGE_IDLE,
        title: 'Battery Storage Inactive',
        refreshable: true,
        secondaryOrder: 3,
        icon: (size: IconSize) => <IconBatteryEmpty size={size}/>,
        troubleshooting: <BatteryStorageInactiveTroubleshooting/>,
        description: 'The AC Battery(ies) charging state has not changed in 72+ hours.'
    },
    {
        value: SystemStatusEnum.GATEWAY_CRITICAL,
        title: 'Gateway Critical',
        refreshable: true,
        secondaryOrder: 4,
        icon: (size: IconSize) => <IconModem size={size}/>,
        troubleshooting: <SystemTier1DefaultTroubleshooting/>,
        description: 'The gateway stopped reporting more than 8 days ago, indicating possible connectivity or' +
            ' hardware problems. Since gateway issues extending beyond a week usually point to serious, ongoing' +
            ' component malfunctions or necessary changes in the connection setup, these are critical concerns that' +
            ' demand prompt manual intervention.'
    },
    {
        value: SystemStatusEnum.NEVER_CONNECTED,
        title: 'Never Connected',
        refreshable: true,
        secondaryOrder: 8,
        icon: (size: IconSize) => <IconWifiOff size={size}/>,
        troubleshooting: <SystemTier1DefaultTroubleshooting/>,
        description: 'One or more systems have been enrolled but not yet internet connected.'
    },
    {
        value: SystemStatusEnum.COMPONENT_ERROR,
        title: 'Component Error',
        refreshable: true,
        secondaryOrder: 9,
        icon: (size: IconSize) => <IconTool01 size={size}/>,
        troubleshooting: <SystemTier1DefaultTroubleshooting/>,
        description: 'A malfunction has been identified in one or more key components: the Inverter, PVL, or Battery.' +
            ' To diagnose and address this issue, please refer to the Device Details page for specific error codes.'
    },
    {
        value: SystemStatusEnum.BATTERY_COMMS,
        title: 'Battery Comms',
        refreshable: true,
        secondaryOrder: 10,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconMessageXCircle size={size}/>,
        description: 'Loss of communication to one or more inverter blocks.'
    },
    {
        value: SystemStatusEnum.BATTERY_FAULT,
        title: 'Battery Fault',
        refreshable: true,
        secondaryOrder: 11,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconBatteryEmpty size={size}/>,
        description: 'One or more inverter blocks are in a faulted state.'
    },
    {
        value: SystemStatusEnum.BLACK_START_FAILURE,
        title: 'Black Start Failure',
        refreshable: true,
        secondaryOrder: 12,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconSlashCircle01 size={size}/>,
        description: 'The battery system was unable to black start.'
    },
    {
        value: SystemStatusEnum.CONTACTOR_UNRESPONSIVE_CLOSE,
        title: 'Contactor Unresponsive Close',
        refreshable: true,
        secondaryOrder: 13,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconGitConnect size={size}/>,
        description: 'The islanding controller was unable to open the islanding contactor/circuit breaker.'
    },
    {
        value: SystemStatusEnum.GENERATOR_FAULTED,
        title: 'Generator Faulted',
        refreshable: true,
        secondaryOrder: 14,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconSpeedometer04 size={size}/>,
        description: 'The generator is in a faulted state.'
    },
    {
        value: SystemStatusEnum.INVERTER_NOT_GOING_TO_VF,
        title: 'Inverter Not Going To Vf',
        refreshable: true,
        secondaryOrder: 15,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconGrid size={size}/>,
        description: 'The battery system is not transitioning to grid-forming mode.'
    },
    {
        value: SystemStatusEnum.ISLANDING_CONTROLLER_MIA,
        title: 'Islanding Controller MIA',
        refreshable: true,
        secondaryOrder: 16,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconMessageXCircle size={size}/>,
        description: 'Loss of communications with the islanding controller.'
    },
    {
        value: SystemStatusEnum.OPTICASTER_EXE,
        title: 'Opticaster Exe',
        refreshable: true,
        secondaryOrder: 17,
        manufacturer: InverterManufacturerEnum.TESLA,
        icon: (size: IconSize) => <IconGitBranch01 size={size}/>,
        description: 'Opticaster failed to run on the Tesla Site Controller, and the battery system is not being dispatched.'
    }
].filter(({ manufacturer }) => manufacturer !== InverterManufacturerEnum.TESLA || feature('tesla-integration'))
